import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';

interface ProtectedRouteProps {
  redirectTo?: string;
  requireRole?: 'admin' | 'channel_operator' | 'viewer';
}

/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login if user is not authenticated.
 * Can optionally require specific roles.
 */
export default function ProtectedRoute({
  redirectTo = '/login',
  requireRole,
}: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore();

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check role if required
  if (requireRole && user) {
    const roleHierarchy = {
      admin: 3,
      channel_operator: 2,
      viewer: 1,
    };

    const userRoleLevel = roleHierarchy[user.role];
    const requiredRoleLevel = roleHierarchy[requireRole];

    if (userRoleLevel < requiredRoleLevel) {
      // User doesn't have required role, redirect to dashboard with error
      return <Navigate to="/dashboard?error=insufficient_permissions" replace />;
    }
  }

  // User is authenticated and has required role (if specified)
  return <Outlet />;
}
